import {
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpDownloadProgressEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import {
  Injectable,
  makeStateKey,
  StateKey,
  TransferState,
} from '@angular/core';
import { catchError, lastValueFrom, throwError, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { PlatformService } from './platform.service';

const defaultAPIErrorMessage = 'Something went wrong';

export class ApiResponse<T> {
  error: string;
  data: T;
  success: boolean;

  constructor(data: T, error?: string) {
    this.data = data;
    this.error = error || '';
    this.success = !error;
  }
}

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  constructor(
    private http: HttpClient,
    // private auth: AuthService,
    private nav: Router,
    private cookie: CookieService,
    private transferState: TransferState,
    private platform: PlatformService
  ) {}

  mockGet(url: string) {
    return this.http.get(url);
  }

  async get<T>(
    url: string,
    params: { [key: string]: any } | null,
    cache?: number,
    useCookie?: boolean
  ) {
    try {
      const requestUrl = this.getUrl(url, params);
      const stateKey: StateKey<ApiResponse<T>> = makeStateKey<ApiResponse<T>>(
        'transfer-' + requestUrl
      );

      // If it's in TransferState (on client), use it and remove from TransferState
      if (this.transferState.hasKey(stateKey)) {
        const data = this.transferState.get<ApiResponse<T>>(
          stateKey,
          {} as ApiResponse<T>
        );
        this.transferState.remove(stateKey);
        return data;
      }

      if (cache) {
        const cached = this.getCache(requestUrl, useCookie);
        if (cached) return cached as ApiResponse<T>;
      }

      const res = this.http
        .get(requestUrl, {
          headers: this.getHeaders(),
        })
        .pipe(catchError(this.catchError.bind(this)));

      const data = (await lastValueFrom(res)) as ApiResponse<T>;

      if (this.platform.isServer()) {
        this.transferState.set(stateKey, data);
      }
      
      if (cache) {
        this.setCache(requestUrl, data, cache, useCookie);
      }

      return data;
    } catch (error) {
      if (this.platform.isServer()) {
        console.error(`[SSR HTTP] Error fetching ${url}:`, error);
      }
      return { error } as ApiResponse<T>;
    }
  }

  async post<T>(
    url: string,
    params: { [key: string]: any } | null,
    body: any,
    cache?: number,
    useCookie?: boolean
  ) {
    try {
      let requestUrl = this.getUrl(url, params);
      if (cache) {
        const cached = this.getCache(requestUrl, useCookie);
        if (cached) return cached as ApiResponse<T>;
      }

      const res = this.http
        .post(requestUrl, body, {
          headers: this.getHeaders(),
        })
        .pipe(catchError(this.catchError.bind(this)));

      const data = (await lastValueFrom(res)) as ApiResponse<T>;

      if (cache) {
        this.setCache(requestUrl, data, cache, useCookie);
      }

      return data;
    } catch (error) {
      return { error } as ApiResponse<T>;
    }
  }

  async put<T>(
    url: string,
    params: { [key: string]: any } | null,
    body: any,
    progress?: (message: string) => void
  ) {
    try {
      const res = this.http
        .put(this.getUrl(url, params), body, {
          headers: this.getHeaders(),
        })
        .pipe(catchError(this.catchError.bind(this)));

      return (await lastValueFrom(res)) as unknown as ApiResponse<T>;
    } catch (error) {
      return { error } as ApiResponse<T>;
    }
  }

  async delete<T>(
    url: string,
    params: { [key: string]: any } | null,
    body: any
  ) {
    try {
      const res = this.http
        .delete(this.getUrl(url, params), {
          headers: this.getHeaders(),
          body,
        })
        .pipe(catchError(this.catchError.bind(this)));

      return (await lastValueFrom(res)) as unknown as ApiResponse<T>;
    } catch (error) {
      return { error } as ApiResponse<T>;
    }
  }

  async uploadFile<T>(
    url: string,
    params: { [key: string]: any } | null,
    body: any,
    progressHandler: (message: string) => void
  ) {
    try {
      const res = this.http
        .post(this.getUrl(url, params), body, {
          headers: this.getHeaders(),
          reportProgress: true,
          observe: 'events',
          responseType: 'text',
        })
        .pipe(
          tap((response) => this.getProgress(response, progressHandler)),
          catchError(this.catchError.bind(this))
        );

      return this.getJSON(await lastValueFrom(res)) as ApiResponse<T>;
    } catch (error) {
      return { error } as ApiResponse<T>;
    }
  }

  private getJSON<T>(res: HttpEvent<string>) {
    if (res.type === HttpEventType.Response) {
      const response = res as any;
      const lastMessage = this.getLastMessage(response.body);
      const body = JSON.parse(lastMessage ?? '{}');
      return body as ApiResponse<T>;
    }
    return { error: 'Something went wrong' } as ApiResponse<T>;
  }

  private getLastMessage(message: string) {
    return message.split('\n\n').pop();
  }

  private getProgress(
    progress: HttpEvent<any>,
    progressHandler: (message: string) => void
  ) {
    if (progress.type === HttpEventType.UploadProgress && progress.total) {
      const percentDone = Math.round((50 * progress.loaded) / progress.total);
      progressHandler(percentDone.toString());
    }

    // Get download progress
    if (progress.type === HttpEventType.DownloadProgress) {
      const downloadProgress = progress as HttpDownloadProgressEvent;
      const lastMessage = this.getLastMessage(
        downloadProgress.partialText ?? ''
      );
      const percentDone = Math.round(
        50 + (50 * parseFloat(lastMessage ?? '0')) / 100
      );
      progressHandler(percentDone.toString());
    }

    if (progress.type === HttpEventType.Response) {
      progressHandler('100');
    }
  }

  private getUrl(url: string, params: { [key: string]: any } | null) {
    if (params) {
      const keys = Object.keys(params);
      if (keys.length > 0) {
        url += '?';
        keys.forEach((key) => {
          url += `${key}=${params[key]}&`;
        });
        url = url.slice(0, -1);
      }
    }

    return this.joinPath(environment.serverUrl, 'api', url);
  }

  private getHeaders() {
    return {
      'Content-Type': 'application/json',
      // 'User-Agent': 'Googlebot/2.1 (+http://www.google.com/bot.html)'
    };
  }

  private catchError(err: HttpErrorResponse) {
    let errorMsg = err.error.error;
    if (err.status === 401) {
      this.nav.navigateByUrl('/login');
      errorMsg = 'Forbidden access';
    } else {
      errorMsg = errorMsg ?? 'Something went wrong';
    }

    const error = new Error(errorMsg);
    return throwError(() => error);
  }

  // Join path
  private joinPath(...paths: string[]) {
    let result = '';
    paths.forEach((path) => {
      result += path.startsWith('http')
        ? path
        : path.startsWith('/')
        ? path
        : `/${path}`;
    });
    return result;
  }

  // Set cache
  private setCache(
    key: string,
    value: any,
    duration: number,
    useCookie?: boolean
  ) {
    // Only use sessionStorage in browser
    if (!this.platform.isBrowser()) return;
    
    if (!useCookie) {
      const expire = Date.now() + duration * 60 * 1000;
      sessionStorage.setItem(key, JSON.stringify({ expire, value }));
    } else {
      //duration in minutes
      this.cookie.set(
        key,
        JSON.stringify(value),
        new Date(Date.now() + duration * 60 * 1000),
        '/',
        undefined,
        true,
        'Strict'
      );
    }
  }

  // Get cache
  private getCache(key: string, useCookie?: boolean) {
    // Only use sessionStorage in browser
    if (!this.platform.isBrowser()) return null;
    
    if (!useCookie) {
      const data = sessionStorage.getItem(key);
      if (data) {
        const { expire, value } = JSON.parse(data);
        if (expire > Date.now()) {
          return value;
        }
        // If expired, remove from cache
        sessionStorage.removeItem(key);
      }
      return null;
    } else {
      const data = this.cookie.get(key);
      return data ? JSON.parse(data) : null;
    }
  }
}
